import { z } from 'zod'

export const USERNAME_MIN_LENGTH = 3
export const USERNAME_MAX_LENGTH = 20

export const STRONG_PASSWORD_RULES = {
	minLength: 12,
	upper: /[A-Z]/,
	lower: /[a-z]/,
	number: /[0-9]/,
	special: /[^A-Za-z0-9]/,
} as const

export function evaluatePasswordRules(password: string) {
	return {
		length: password.length >= STRONG_PASSWORD_RULES.minLength,
		upper: STRONG_PASSWORD_RULES.upper.test(password),
		lower: STRONG_PASSWORD_RULES.lower.test(password),
		number: STRONG_PASSWORD_RULES.number.test(password),
		special: STRONG_PASSWORD_RULES.special.test(password),
	}
}

export const StrongPasswordSchema = z
	.string({ required_error: 'Password is required' })
	.min(STRONG_PASSWORD_RULES.minLength, {
		message: `Must be at least ${STRONG_PASSWORD_RULES.minLength} characters`,
	})
	.refine((val) => new TextEncoder().encode(val).length <= 72, {
		message: 'Password is too long',
	})
	.refine((val) => STRONG_PASSWORD_RULES.upper.test(val), {
		message: 'Must contain an uppercase letter',
	})
	.refine((val) => STRONG_PASSWORD_RULES.lower.test(val), {
		message: 'Must contain a lowercase letter',
	})
	.refine((val) => STRONG_PASSWORD_RULES.number.test(val), {
		message: 'Must contain a number',
	})
	.refine((val) => STRONG_PASSWORD_RULES.special.test(val), {
		message: 'Must contain a special character',
	})

/**
 * Generate a cryptographically random 16-character password guaranteed to
 * satisfy StrongPasswordSchema. Used by the "auto-generate" mode in the
 * admin create-user flow.
 */
export function generateStrongPassword(): string {
	const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ' // excludes ambiguous I, O
	const lower = 'abcdefghijkmnpqrstuvwxyz' // excludes ambiguous l, o
	const number = '23456789' // excludes ambiguous 0, 1
	const special = '!@#$%^&*?-_+='
	const all = upper + lower + number + special

	// crypto.getRandomValues is available in Node 19+ and all modern browsers
	const rand = (n: number): number => {
		const buf = new Uint32Array(1)
		crypto.getRandomValues(buf)
		return buf[0] % n
	}
	const pick = (src: string) => src[rand(src.length)]

	const guaranteed = [pick(upper), pick(lower), pick(number), pick(special)]
	while (guaranteed.length < 16) guaranteed.push(pick(all))

	// Fisher–Yates shuffle so required chars aren't always first
	for (let i = guaranteed.length - 1; i > 0; i--) {
		const j = rand(i + 1)
		;[guaranteed[i], guaranteed[j]] = [guaranteed[j], guaranteed[i]]
	}
	return guaranteed.join('')
}

export const UsernameSchema = z
	.string({ required_error: 'Username is required' })
	.min(USERNAME_MIN_LENGTH, { message: 'Username is too short' })
	.max(USERNAME_MAX_LENGTH, { message: 'Username is too long' })
	.regex(/^[a-zA-Z0-9_]+$/, {
		message: 'Username can only include letters, numbers, and underscores',
	})
	// users can type the username in any case, but we store it in lowercase
	.transform((value) => value.toLowerCase())

export const PasswordSchema = z
	.string({ required_error: 'Password is required' })
	.min(6, { message: 'Password is too short' })
	// NOTE: bcrypt has a limit of 72 bytes (which should be plenty long)
	// https://github.com/epicweb-dev/epic-stack/issues/918
	.refine((val) => new TextEncoder().encode(val).length <= 72, {
		message: 'Password is too long',
	})

export const NameSchema = z
	.string({ required_error: 'Name is required' })
	.min(3, { message: 'Name is too short' })
	.max(40, { message: 'Name is too long' })

export const EmailSchema = z
	.string({ required_error: 'Email is required' })
	.email({ message: 'Email is invalid' })
	.min(3, { message: 'Email is too short' })
	.max(100, { message: 'Email is too long' })
	// users can type the email in any case, but we store it in lowercase
	.transform((value) => value.toLowerCase())

export const PasswordAndConfirmPasswordSchema = z
	.object({ password: PasswordSchema, confirmPassword: PasswordSchema })
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				path: ['confirmPassword'],
				code: 'custom',
				message: 'The passwords must match',
			})
		}
	})
